import { DestroyRef, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface Toast {
  readonly id: number;
  readonly message: string;
  readonly tone: ToastTone;
  readonly durationMs: number;
}

export interface ShowToastOptions {
  readonly tone?: ToastTone;
  /** Auto-dismiss delay in ms. */
  readonly durationMs?: number;
}

const DEFAULT_DURATION_MS = 5000;

/**
 * Signal-based toast queue: `show()` pushes, `activeToasts` is what a mounted `<app-toast-host>`
 * reads, `dismiss()` (manual or timer-driven) removes.
 *
 * Ticket #936 exists because of a measured failure in the source storefront, and this service's
 * shape is a direct response to it: `ToastService.show()` is called 25 times across 8 non-spec
 * files there, and NO template anywhere reads `activeToasts` -- zero matches for `activeToasts` or
 * the literal word "toast" across all 63 `.html` files in that codebase. Every one of those 25
 * calls pushes a toast into a signal nothing renders. Nothing throws, the call site type-checks,
 * the service's own state is internally consistent -- the user simply never sees anything. That
 * failure is invisible from this file alone; it is only visible from the template side, which is
 * exactly why AC1 of this ticket requires `<app-toast-host>` to be MOUNTED in the layout shell,
 * not merely built. This file is the half that cannot detect the bug on its own.
 *
 * `activeToasts` is exposed read-only (`signal.asReadonly()`) so a consumer can react to the queue
 * but only this service can mutate it -- the same reasoning `CatalogService` uses for its own
 * state signals.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private readonly toasts = signal<readonly Toast[]>([]);
  readonly activeToasts = this.toasts.asReadonly();

  private nextId = 0;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  constructor() {
    // A root-provided service is recreated once per SSR request (a fresh application, a fresh
    // injector, per the house SSR pattern) rather than once per process. Without this, a timer
    // scheduled mid-request would keep the request's injector alive past the response, which is
    // the server-side version of the leak `useCarousel`'s `destroyRef.onDestroy` closes for its
    // own interval.
    this.destroyRef.onDestroy(() => {
      for (const timer of this.timers.values()) clearTimeout(timer);
      this.timers.clear();
    });
  }

  /** Queues a toast and returns its id, which `dismiss()` accepts. */
  show(message: string, options: ShowToastOptions = {}): number {
    const id = this.nextId++;
    const toast: Toast = {
      id,
      message,
      tone: options.tone ?? 'info',
      durationMs: options.durationMs ?? DEFAULT_DURATION_MS,
    };

    this.toasts.update((list) => [...list, toast]);

    // SSR renders once per request with no live viewer to auto-dismiss a toast for, and no
    // browser tab to schedule the timer against -- so, per the house SSR-safe pattern, the
    // auto-expiry timer is browser-only. This is a direct `isPlatformBrowser` check rather than
    // `afterNextRender`: that hook fires once after a component's first render and does not fit a
    // timer armed from an arbitrary, repeatable `show()` call with no render of its own to hang
    // off of. `useCarousel`'s interval is the case `afterNextRender` DOES fit, because it starts
    // once, at mount.
    if (isPlatformBrowser(this.platformId)) {
      const timer = setTimeout(() => this.dismiss(id), toast.durationMs);
      this.timers.set(id, timer);
    }

    return id;
  }

  /** Removes a toast immediately -- called by its auto-expiry timer, or by its dismiss control. */
  dismiss(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    this.toasts.update((list) => list.filter((toast) => toast.id !== id));
  }
}
